import { useLayoutEffect, useRef, useState } from 'react'

/**
 * A function that keeps one identity for the component's lifetime and calls the latest `handler`.
 * For handlers handed to memoised children (React.memo): one that closes over state is a new
 * function on most renders, and would re-render every child it reaches.
 *
 * Call it from events and effects, not during render: it calls the handler of the last committed
 * render.
 */
export const useStableHandler = <Args extends unknown[], Result>(
  handler: (...args: Args) => Result
): ((...args: Args) => Result) => {
  const latest = useRef(handler)
  useLayoutEffect(() => {
    latest.current = handler
  })
  const [stable] = useState(
    () =>
      (...args: Args) =>
        latest.current(...args)
  )
  return stable
}
